import { useSelector } from 'react-redux'
import moment from 'moment'
import stripeWeb from '@src/config/stripe.web'
import { useShouldShowByosOnboardingTutorials } from '@src/screens/OwnSensorOnboarding/hooks'
import { userSelector } from '@src/selectors/app'
import { Address, PromotionCode, SubscriptionCheckoutOptionKind } from '@src/types'
import { useDispatchAsync } from '@src/utils'
import { useShouldShowPurchaseFlow } from '@src/utils/hooks'
import { CreateSubscriptionMutation } from '../../graphql/purchaseSubscription.generated'
import { trackSubscription } from '../trackSubscription'

export const usePurchaseSubscription = () => {
  const dispatchAsync = useDispatchAsync()
  const user = useSelector(userSelector)
  const shouldShowPurchaseFlow = useShouldShowPurchaseFlow()
  const shouldShowOnboardingTutorials = useShouldShowByosOnboardingTutorials()
  const onboardingIsInProgress = shouldShowPurchaseFlow || shouldShowOnboardingTutorials

  const { email, fullName } = user || {}

  return async ({
    productKey,
    paymentMethodId,
    checkoutOptionKinds,
    promotionCode,
    address,
  }: {
    productKey: string
    paymentMethodId?: string
    checkoutOptionKinds?: SubscriptionCheckoutOptionKind[]
    promotionCode?: PromotionCode | null
    address: Pick<Address, 'name' | 'street' | 'street2' | 'city' | 'state' | 'country' | 'zipCode'>
  }) => {
    const purchaseResponse = await dispatchAsync<CreateSubscriptionMutation>({
      type: 'marketplace/purchaseSubscription',
      payload: {
        providerPaymentMethodId: paymentMethodId,
        product: productKey,
        address,
        email,
        fullName,
        providerPromotionCodeId: promotionCode?.providerId ?? null,
        checkoutOptionKinds,
      },
    })

    const {
      createSubscription: {
        subscription: {
          id: subscriptionId,
          price: subscriptionTotal,
          primaryProduct: { id: primaryProductId, title },
          checkoutOptions,
          trialEndAt,
        },
        clientSecret,
      },
    } = purchaseResponse

    const onSubscriptionCreateSuccess = () => {
      trackSubscription({
        subscriptionTotal,
        subscriptionId,
        onboarding: onboardingIsInProgress,
        primaryProductId,
        nickname: title,
        trial: !!trialEndAt && moment(trialEndAt).isAfter(moment()),
        checkoutOptions,
      })

      return purchaseResponse.createSubscription.subscription
    }

    if (!clientSecret) {
      return onSubscriptionCreateSuccess()
    }

    const stripe = ((await stripeWeb.getStripe()) as unknown) as {
      handleNextAction: (params: {
        clientSecret: string
      }) => Promise<{
        error?: Error
        paymentIntent?: { status: string }
      }>
    }

    const { error: cardActionError, paymentIntent } = await stripe.handleNextAction({
      clientSecret,
    })

    if (paymentIntent?.status === 'succeeded') {
      return onSubscriptionCreateSuccess()
    }

    if (cardActionError) {
      throw new Error("Couldn't complete transaction!")
    }
  }
}
